import React, { FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SearchRemoveIcon } from "@bookingcom/bui-assets-react/streamline/index.js";
import {
  Accordion,
  Button,
  Card,
  HiddenVisually,
  Icon,
  Link,
  List,
  Text,
  Title,
  useViewport
} from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { I18nChildContext, useI18n, Trans } from "@bookingcom/lingojs-react";
import { UIFlightData, UISearchResultsBanner } from "@flights/types";
import useCreateUrl from "../../../hooks/useCreateUrl";
import useSearchFilters from "../../../hooks/useSearchFilters";
import { getExpandedSearchAirports } from "../../../store/selectors/expandedSearch";
import { isSirfAvailable } from "../../../store/selectors/sirf";
// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore } from "../../../store";
import { Sidebar } from "../Container";
import FlightCardDesktop from "../FlightCard/FlightCard.desktop";
import SearchBaggagePolicies from "../SearchBaggagePolicies";
import SearchFiltersSummary from "../SearchFiltersSummary";
import { SearchPaginationDesktop } from "../SearchPagination";
import SearchTabs from "../SearchTabs";
import SirfInfoBanner from "../SirfInfoBanner";
import SkeletonSearchDesktop, { SkeletonSearchContent } from "../Skeleton/SkeletonPages/SkeletonSearch.desktop";
import { DESKTOP_SIDEBAR_WIDTH, FILTERS } from "../../../constants";
import useClientCachedURL from "../../../hooks/useClientCachedURL";
import { DestinationUkraineWarning } from "../TravelAlert";
import NoDirectFlightsBanner from "./components/NoDirectFlightsBanner";
import { NoResultsFound } from "./components/NoResultsFound";
import RefreshResults from "./components/RefreshResults";
import { RemoveDirectFlights } from "./components/RemoveDirectFlights";
import { TooManyFilters } from "./components/TooManyFilters";
import { isOfMetaOrigin } from "../../../utils/marketing-url-params";
import useGlobalContext from "../../../hooks/useGlobalContext";
import useEventTracking from "../../../hooks/useEventTracking";
import DSABanner from "../DSABanner/DSABanner";
import { getLocationParams } from "./utils";
import GeniusTCBanner from "../../../cross-sell/components/GeniusTCBanner";
import Frame from "../Frame";
import { FlightCampaignTCBanner } from "../FlightCampaignTCBanner";
import { flightsSessionStore, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { FlexibleDateBestPriceDesktop } from "../FlexibleDateBestPrice";
import styles from "./SearchResults.desktop.module.css";
import { useFlexDatesRectrictedPOS } from "../FlexibleDateBestPrice/useFlexDatesRectrictedPOS";
import useProfiler from "../../../hooks/useProfiler";
import SearchResultBanner from "../SearchResultBanner/SearchResultBanner";
import { RemoteAppleCardSR, WishlistWidgetLoader } from "../../remote";
import NoSSR from "../../../app/NoSSR";
import flights_apex_web_ancillaries_micro_conversion_aa from "../../../utils/experiments/apex/flights_apex_web_ancillaries_micro_conversion_aa";
import useOfferCartOrderData from "../../../hooks/useOfferCartOrderData";
import useAlternativeFaresParams from "../../../hooks/useAlternativeFaresParams";
import { useSelector } from "react-redux";
import flights_ace_web_esim_banner, {
  useTrackEsimBannerStages
} from "../../../utils/experiments/ace/flights_ace_web_esim_banner";
import EsimBanner from "../../../ancillaries/components/Esim/EsimBanner";
import { getEsimEligibilitySearch } from "../../../ancillaries/store/esim/selectors";
import useIsInViewport from "../../../hooks/useIsInViewport";
import Wrapper from "../../atoms/Wrapper";
import { useEsimSearch } from "../../../ancillaries/hooks/Esim/useEsimFetch";
import AiSummaryDesktop from "./components/AiSummary.desktop";
import useLocaleContext from "../../../hooks/useLocaleContext";
import useTrackFlightsFlexiBannerSourceChange from "../../../utils/experiments/funnel/flights_web_flexi_banner_source_change_ml";
import useFlightsWebSrNewFlexPriceMlModel from "../../../utils/experiments/funnel/flights_web_sr_new_flex_price_ml_model";
import SearchFiltersDesktop from "../SearchFilters/SearchFilters.desktop";
import { DSATraceabilityPopover } from "../FlightDetailsInner/components/DSATraceability/DSATraceability.popover";
import useTrackFlightsWeb2StepRanker from "../../../utils/experiments/funnel/flights_web_2_step_ranker";
import flights_split_bounds_us, {
  useTrackSplitBoundsStages
} from "../../../utils/experiments/funnel/flights_split_bounds_us";
import { trackWithDefaultStage } from "@flights/et-universal";
import { FilterRow } from "../SearchFiltersSummary/SearchFiltersSummary";
import { FilterKey, getFiltersMeta } from "../../../constants/filtersMeta";
import { useFiltersReset } from "../../../hooks/useFiltersReset";
import SearchSortSelector from "./components/SearchSortSelector";
import flights_web_remove_fastest_sorting, {
  useTrackRemoveFastestSorting
} from "../../../utils/experiments/funnel/flights_web_remove_fastest_sorting";
import { useGetAvailableFilters } from "./components/boundsFilterHooks";
import flights_web_filters_width_desktop, {
  useTrackReducedFiltersWidth
} from "../../../utils/experiments/funnel/flights_web_filters_width_desktop";
import { useAppliedFilters } from "../../../hooks/search/useAppliedFilters";
import FlightCard2Desktop from "../FlightCard/FlightCard2.desktop";
import flights_web_flight_card_update_desktop, {
  useTrackFlightCardUpdateDesktop,
  useTrackFlightCardUpdateDSABannerDesktop
} from "../../../utils/experiments/funnel/flights_web_flight_card_update_desktop";
import {
  trackPredictionExperimentCounter,
  useTrackFlexibilityPrediction
} from "../../../utils/experiments/funnel/flights_web_aa_flexibility_prediction";

type SearchResultsListProps = {
  flights: UIFlightData[];
  onSelectFlight: (flight: UIFlightData) => void;
  hasAppliedFilters?: boolean;
};

const _SearchResultsList = ({ flights, onSelectFlight }: SearchResultsListProps) => {
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const {
    search,
    searchResults: { campaignBanner, remoteBanners, displayOptions }
  } = store;
  const isRestricedFlexDatePOS = useFlexDatesRectrictedPOS();
  const params = new URLSearchParams(location.search);
  const currentSearchResultsPageNumber = params.get("page");

  /* start - flights_split_bounds_us */
  useTrackSplitBoundsStages();
  /* end - flights_split_bounds_us */

  const { trackStages } = useTrackFlightsFlexiBannerSourceChange(); // flights_web_flexi_banner_source_change_ml
  const { trackStages: trackNewStages } = useFlightsWebSrNewFlexPriceMlModel(); // flights_web_sr_new_flex_price_ml_model"
  useEffect(() => {
    trackStages();
    trackNewStages();
  }, [trackStages, trackNewStages]);

  /** start - flights_ace_web_esim_banner */
  const esimBannerRef = useRef<HTMLDivElement>(null);
  const eSimInViewport = useIsInViewport(esimBannerRef.current);
  const isEsimEligible = !!useSelector(getEsimEligibilitySearch);
  const showEsimBanner = isEsimEligible && !!flights_ace_web_esim_banner.trackWithDefaultStage();
  const ESIM_BANNER_SEARCH_RESULTS_INDEX = 6;
  const fetchEsim = useEsimSearch();
  /** end - flights_ace_web_esim_banner */
  useTrackEsimBannerStages(isEsimEligible);
  /* start - flights_ace_web_esim_banner */
  useEffect(() => {
    void fetchEsim();
  }, [fetchEsim]);

  useEffect(() => {
    if (eSimInViewport) {
      flights_ace_web_esim_banner.stages.viewed_esim_banner_sr_page();
      flights_ace_web_esim_banner.goals.viewed_esim_banner();
    }
  }, [eSimInViewport]);
  /* end - flights_web_ace_esim_banner */

  //There isn't a page param for the first search
  const isFirstSearchResultsPage = currentSearchResultsPageNumber ? Number(currentSearchResultsPageNumber) === 1 : true;

  useEffect(() => {
    flights_apex_web_ancillaries_micro_conversion_aa.trackSearchResultsStages(flights);
  }, [flights]);

  const shouldShowFlexDates =
    !displayOptions?.hideFlexiblePricesBanner &&
    flights.length >= 5 &&
    store.search.cabinClass === "ECONOMY" &&
    isFirstSearchResultsPage &&
    !isRestricedFlexDatePOS &&
    !isOfMetaOrigin();

  const trackFlightCardUpdateDesktop = useTrackFlightCardUpdateDesktop();
  flights_web_flight_card_update_desktop.stages.users_see_flight_cards();

  return (
    <div>
      <List attributes={{ style: { padding: 0 } }}>
        {flights.map((flight: UIFlightData, index: number) => {
          return (
            <div key={`${index}_${flight.token}`}>
              {isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_WHITE_LABEL_TC_BANNER_IN_FE") &&
              campaignBanner &&
              index === campaignBanner.position ? (
                <Frame mt={2} mb={4}>
                  <FlightCampaignTCBanner campaignBanner={campaignBanner} />
                </Frame>
              ) : null}
              {Array.isArray(remoteBanners) &&
              remoteBanners.includes("APPLE_CARD_BANNER_SR") &&
              index === flights.length - 1 ? (
                <RemoteAppleCardSR />
              ) : undefined}
              <div id={`flightcard-${index}`} data-testid="searchresults_card">
                {trackFlightCardUpdateDesktop() ? (
                  <FlightCard2Desktop flight={flight} index={index} onClick={onSelectFlight} />
                ) : (
                  <FlightCardDesktop
                    flight={flight}
                    onClick={onSelectFlight}
                    pricePerPerson
                    index={index}
                    searchType={search.type}
                  />
                )}
              </div>
              {shouldShowFlexDates && index === 0 ? <FlexibleDateBestPriceDesktop /> : null}
              {/* If GeniusCampaign is Enabled, show the Genius TC campaign on 3rd position */}
              {!isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_WHITE_LABEL_TC_BANNER_IN_FE") &&
              index === 1 &&
              campaignBanner ? (
                <Frame mt={2} mb={4}>
                  <GeniusTCBanner campaignBanner={campaignBanner} />
                  <SearchTrafficAAGeniusTrackerDesktop />
                </Frame>
              ) : null}
              {/**
               * If the position is larger than the number of cards displayed in the SR page,
               * then the T&C banner is shown after the last card.
               * More info: https://jira.booking.com/jira/browse/FPRICING-809
               */}
              {isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_WHITE_LABEL_TC_BANNER_IN_FE") &&
              campaignBanner &&
              campaignBanner.position >= flights.length && // campaign banner position is larger than the number of cards
              index >= flights.length - 1 ? ( // after the last flight card
                <Frame mt={2} mb={4}>
                  <FlightCampaignTCBanner campaignBanner={campaignBanner} />
                </Frame>
              ) : null}
              {/* start - flights_ace_web_esim_banner */}
              {showEsimBanner && index === ESIM_BANNER_SEARCH_RESULTS_INDEX ? (
                <Wrapper
                  elementRef={esimBannerRef}
                  noBorder
                  noPadding
                  noPaddingVertical
                  style={{
                    paddingBottom: "8px",
                    paddingBlockStart: "0px"
                  }}
                >
                  <EsimBanner context="search" />
                </Wrapper>
              ) : null}
              {/* end - flights_ace_web_esim_banner */}
            </div>
          );
        })}
      </List>
    </div>
  );
};

const SearchResultsList = React.memo(_SearchResultsList);

// Temp fix for carousel increasing width
const attributes = { style: { width: `calc(100% - ${DESKTOP_SIDEBAR_WIDTH}px)` } };
const attributesRes = { style: { width: "100%", padding: 0 } };

const SearchResultsWithSidebarAndTabs: FC<{
  children: React.ReactNode;
  humanRightBanner?: UISearchResultsBanner;
}> = ({ children, humanRightBanner }) => {
  const [displayedFiltersCount, setDisplayedFiltersCount] = useState(3);

  const i18n = useI18n();
  const { locale } = useLocaleContext();
  const { isLarge } = useViewport();

  const appliedFilters = useAppliedFilters();
  const filtersCount = appliedFilters.length;

  const filtersMeta = getFiltersMeta(i18n);

  const isEligibleForAiSummary = locale?.code.toLowerCase().startsWith("en-");
  const isResponsiveFixes = !isLarge && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);

  const filtersArea = useMemo(() => {
    return (
      <>
        {isResponsiveFixes ? null : isEligibleForAiSummary ? <AiSummaryDesktop /> : null}

        {!isResponsiveFixes ? (
          <Frame pb={3}>
            <SearchFiltersSummary />
          </Frame>
        ) : null}
        <SearchFiltersDesktop />
        <DSATraceabilityPopover />
      </>
    );
  }, [isEligibleForAiSummary, isResponsiveFixes]);

  const { resetFilter, resetAllFilters } = useFiltersReset();

  useTrackReducedFiltersWidth();
  const isReducedWidth = !!flights_web_filters_width_desktop.trackWithDefaultStage();

  const trackFlightCardUpdateDSABannerDesktop = useTrackFlightCardUpdateDSABannerDesktop();

  return (
    <div className={styles.searchResults} data-testid="searchresults_list">
      <NoSSR>
        <Frame direction="row">
          {!isResponsiveFixes ? (
            <Sidebar fixScrollPadding isReducedWidth={isReducedWidth}>
              {filtersArea}
            </Sidebar>
          ) : null}

          <Frame direction="column" grow={1} pb={4} pl={6} attributes={!isResponsiveFixes ? attributes : attributesRes}>
            <HiddenVisually>
              <Text tagName="h2">{i18n.trans(t("a11y_flights_sr__results_hidden_heading"))}</Text>
            </HiddenVisually>

            {isResponsiveFixes ? isEligibleForAiSummary ? <AiSummaryDesktop /> : null : null}
            <SearchResultBanner banner={humanRightBanner} />
            {!trackFlightCardUpdateDSABannerDesktop() ? <DSABanner /> : undefined}
            {isResponsiveFixes ? (
              <Card className={styles.filtersAccordion}>
                <Accordion
                  titleContent={
                    <SearchFiltersSummary isResponsiveFixes setDisplayedFiltersCountProp={setDisplayedFiltersCount} />
                  }
                >
                  <>
                    <div className={styles.appliedFilters}>
                      {filtersCount > 0 && (
                        <Link
                          onClick={resetAllFilters}
                          attributes={{ "data-testid": "search_filters_summary_reset_all" }}
                        >
                          <Trans tag="flights_filters_reset_all_action" />
                        </Link>
                      )}
                      {appliedFilters.slice(0, displayedFiltersCount).map((filter: FilterKey, index) => (
                        <Frame key={index} mb={3}>
                          <FilterRow filterMeta={filtersMeta[filter]} resetFilter={resetFilter} />
                        </Frame>
                      ))}
                    </div>

                    {filtersArea}
                  </>
                </Accordion>
              </Card>
            ) : null}
            <SearchResultsTabsContainer>{children}</SearchResultsTabsContainer>
          </Frame>
        </Frame>
      </NoSSR>
    </div>
  );
};

const SearchResultsTabsContainer = (props: PropsWithChildren<{}>) => {
  const i18n = useI18n() as I18nChildContext;
  const { filteredTotalCount = 0 } = useGetAvailableFilters();

  useTrackRemoveFastestSorting();
  const removeFastestSorting = !!flights_web_remove_fastest_sorting.trackWithDefaultStage();

  return (
    <>
      {removeFastestSorting ? (
        <>
          <Frame direction="row" justifyContent="space-between" alignItems="center">
            <Title
              title={i18n.trans(
                t("flights_sr_sort_chips_title", {
                  variables: {
                    num_flights: filteredTotalCount
                  },
                  num_exception: filteredTotalCount
                })
              )}
              variant="strong_1"
              titleTagName="h2"
            />
            <SearchSortSelector />
          </Frame>
          {props.children}
        </>
      ) : (
        <SearchTabs showBestRankingInfo={true}>{props.children}</SearchTabs>
      )}
    </>
  );
};

type FailedSearchResultsProps = {
  expandToCitySearch: () => void;
  i18n: I18nChildContext;
};

const FailedSearchResults = ({ expandToCitySearch, i18n }: FailedSearchResultsProps) => {
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const { search } = store;
  return (
    <Frame mt={6} mb={6} attributes={{ style: { minHeight: "40vh" } }}>
      <Frame m={3} alignItems="center">
        <Icon svg={SearchRemoveIcon} size="small" className={styles.searchRemoveIcon} />
        <Text variant="headline_2">{i18n.trans(t("flights_search_no_results_header"))}</Text>
      </Frame>
      <Frame alignItems="center">
        <Text variant="body_2">{i18n.trans(t("flights_search_no_results_copy_desktop"))} </Text>
      </Frame>
      {search.type !== "MULTISTOP" && (
        <Frame alignItems="center" m={6}>
          <Button onClick={expandToCitySearch}>{i18n.trans(t("flights_search_no_results_nearbyairports_cta"))}</Button>
        </Frame>
      )}
    </Frame>
  );
};

interface SearchResultsDesktopSectionProps {
  airports: string;
  onSelectFlight: () => void;
  hasAppliedFilters?: boolean;
}

const SearchResultsDesktopSection = ({
  airports,
  onSelectFlight,
  hasAppliedFilters
}: SearchResultsDesktopSectionProps) => {
  const history = useHistory();
  const location = useLocation();
  const { createUrl } = useCreateUrl();
  const { applyFilters } = useSearchFilters();
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const { search, searchResults } = store;
  const { flights = [], subsidizedFaresSummary, personalisationAttributes } = searchResults;
  const i18n = useI18n() as I18nChildContext;
  const { cacheLastSuccessfulSearchUrl } = useClientCachedURL();
  const offerCartOrderData = useOfferCartOrderData();
  const { setAlternativeFaresParams } = useAlternativeFaresParams();
  const { isLoggedIn } = useProfiler();
  const { isLarge } = useViewport();

  const isResponsiveFixes = !isLarge && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);

  useEffect(() => {
    applyFilters(search.filtersDraft);
  }, [search.filtersDraft, applyFilters]);

  /* start - flights_web_2_step_ranker */
  const trackFlightsWeb2StepRanker = useTrackFlightsWeb2StepRanker();
  useEffect(() => {
    trackFlightsWeb2StepRanker.trackStages();
  }, [trackFlightsWeb2StepRanker]);
  /* end - flights_web_2_step_ranker */

  /* start - flights_web_aa_flexibility_prediction */
  useTrackFlexibilityPrediction(personalisationAttributes);
  /* end - flights_web_aa_flexibility_prediction */

  const showFlightDetails = useCallback(
    async (flight: any) => {
      /* start - flights_web_aa_flexibility_prediction */
      trackPredictionExperimentCounter(flight.token);
      /* end - flights_web_aa_flexibility_prediction */
      trackFlightsWeb2StepRanker.onClick(flight.token);
      onSelectFlight();
      flights_split_bounds_us.goals.cta_click();
      offerCartOrderData.set(flight.token, {
        offer: flight.token,
        cabinClass: searchResults.searchCriteria?.cabinClass
      });
      const url = createUrl(`/flights/${airports}/${flight.token}/?${getLocationParams(location.search)}`);
      history.push(url);
    },
    [
      trackFlightsWeb2StepRanker,
      onSelectFlight,
      offerCartOrderData,
      searchResults.searchCriteria?.cabinClass,
      createUrl,
      airports,
      location.search,
      history
    ]
  );

  cacheLastSuccessfulSearchUrl(airports);
  searchResults.fetchedAt && setAlternativeFaresParams("searchedAt", searchResults.fetchedAt);
  return (
    <>
      <NoDirectFlightsBanner />
      <DestinationUkraineWarning />
      {isSirfAvailable(subsidizedFaresSummary) && (
        <SirfInfoBanner
          bannerName="search-results"
          bannerContent={i18n.trans(t("flights_sr_spanish_discount_banner"))}
        />
      )}
      <SearchBaggagePolicies />
      <SearchResultsList flights={flights} onSelectFlight={showFlightDetails} hasAppliedFilters={hasAppliedFilters} />
      <div className={isResponsiveFixes ? styles.paginationRes : ""}>
        <SearchPaginationDesktop />
      </div>
      <RefreshResults flights={flights} />
      {isLoggedIn() ? <WishlistWidgetLoader /> : null}
    </>
  );
};
type ComposedSearchResultsProps = SearchResultsDesktopSectionProps;

const ComposedSearchResults = ({ airports, onSelectFlight }: ComposedSearchResultsProps) => {
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const history = useHistory();
  const location = useLocation();
  const { createUrl } = useCreateUrl();
  const { applyFilters } = useSearchFilters();
  const { requestId } = useGlobalContext();
  const trackV2 = useEventTracking("search_results", requestId);
  const {
    search,
    searchResults: { flights = [], fetchStatus, error, availableFilters, banners },
    flexibleDateMinPrice: { fetchStatus: flexibleDatePriceFetchStatus }
  } = store;
  const appliedFilters = useAppliedFilters();
  const isRestricedFlexDatePOS = useFlexDatesRectrictedPOS();
  const humanRightBanner = banners?.find((banner: UISearchResultsBanner) => banner.id === "LEGAL_NOTICE_HUMAN_RIGHTS");
  useEffect(() => {
    applyFilters(search.filtersDraft);
  }, [search.filtersDraft, applyFilters]);

  // TODO: https://booking.atlassian.net/browse/FUNNEL-2949
  //  Move redirect to redirects.ts if flights_web_remove_fastest_sorting goes full-on
  useEffect(() => {
    if (!!flights_web_remove_fastest_sorting.trackWithDefaultStage()) {
      const query = new URLSearchParams(location.search);
      const sort = query.get("sort");
      if (sort === "FASTEST") {
        query.set("sort", "BEST");
        window.location.href = `${location.pathname}?${query}`;
      }
    }
  }, [location.search, location.pathname]);

  const i18n = useI18n() as I18nChildContext;

  const expandedSearchAirports = useMemo(() => {
    const { searchSegments } = search;
    /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
    /**
     * TODO: Update ExpandedSeach for multistop cases?
     */
    const { from, to } = searchSegments[0];
    return getExpandedSearchAirports(from, to);
  }, [search]);

  const expandToCitySearch = useCallback(() => {
    if (expandedSearchAirports) {
      const { fromCities, toCities, fromLocationName, toLocationName } = expandedSearchAirports;
      const params = new URLSearchParams(location.search);
      params.set("from", fromCities);
      params.set("to", toCities);
      params.set("fromLocationName", fromLocationName);
      params.set("toLocationName", toLocationName);

      history.replace(createUrl(`/flights/${fromCities}-${toCities}/?${params}`));
      window.location.reload();
    }
  }, [history, location, expandedSearchAirports, createUrl]);

  useEffect(() => {
    if (["loading", "initial"].includes(fetchStatus)) return;
    if (flights.length === 0) trackV2("show_empty_search_results");
  }, [flights, fetchStatus, trackV2]);

  const firstLoad = !availableFilters.durationMin;
  // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
  const noFlightsFound = (error && error["code"] === "SEARCH_SEARCHFLIGHTS_NO_FLIGHTS_FOUND") || flights.length === 0;
  const canExtendSearch = appliedFilters.length === 0 && noFlightsFound && expandedSearchAirports;

  const canRemoveDirectFlightsFilter = appliedFilters.includes(FILTERS.stops) && noFlightsFound;
  let resultingComp = undefined;

  const flexibleFetchConditionalStatus = useMemo(() => {
    if (store.search.cabinClass === "ECONOMY" && !isRestricedFlexDatePOS && !isOfMetaOrigin()) {
      return {
        loading: flexibleDatePriceFetchStatus === "initial" || flexibleDatePriceFetchStatus === "loading",
        success: flexibleDatePriceFetchStatus === "success"
      };
    }

    return {
      loading: false,
      success: true
    };
  }, [flexibleDatePriceFetchStatus, isRestricedFlexDatePOS, store.search.cabinClass]);

  if (fetchStatus === "initial" || fetchStatus === "loading" || flexibleFetchConditionalStatus.loading) {
    if (firstLoad) {
      // this is the result of SSR
      return <SkeletonSearchDesktop />;
    }
    // need sidebar from now on and no SSR
    resultingComp = <SkeletonSearchContent />;
  }

  if (fetchStatus === "failed" && canExtendSearch) {
    // this can also be SSR
    return <FailedSearchResults expandToCitySearch={expandToCitySearch} i18n={i18n} />;
  }

  // case of success should go here, i.e. the composed result
  if (fetchStatus === "success") {
    if (flights.length > 0) {
      // No SSR for this case, why not ? because api is called from client side
      resultingComp = (
        <SearchResultsDesktopSection
          airports={airports}
          onSelectFlight={onSelectFlight}
          hasAppliedFilters={appliedFilters.length > 0}
        />
      );
    } else if (canRemoveDirectFlightsFilter) {
      // can be generated from SSR
      return (
        <>
          <SearchResultBanner banner={humanRightBanner} />
          <RemoveDirectFlights />
        </>
      );
    }
  }

  if (!resultingComp) {
    // this is a fallthrough default when everything else fails
    if (appliedFilters.length > 0) {
      // need sidebar here too
      resultingComp = <TooManyFilters />;
    } else {
      return <NoResultsFound />;
    }
  }

  const screenReaderText = () => {
    if (appliedFilters.length > 0 && flights) {
      return i18n.trans(t("a11y_flights_filters_general_updated"));
    }

    if (appliedFilters.length === 0 && flights) {
      return i18n.trans(t("a11y_flights_sr_results_loaded"));
    }

    return "";
  };

  return (
    <SearchResultsWithSidebarAndTabs humanRightBanner={humanRightBanner}>
      {resultingComp}

      <HiddenVisually>
        <div aria-live="polite">{screenReaderText()}</div>
      </HiddenVisually>
    </SearchResultsWithSidebarAndTabs>
  );
};

export const SearchTrafficAAGeniusTrackerDesktop: FC = () => {
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();

  useEffect(() => {
    if (!store.user.userInfo) {
      flightsSessionStore.set("genius_banner_seen", true);
    }
  }, [store.user.userInfo]);

  return null;
};

export default React.memo(ComposedSearchResults);
